import './productPricing.css';
import { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import { ChevronDown, ChevronRight, Copy, GripVertical, Trash2 } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import {
  calculateInvestmentProduct, confirmProductRemoval, duplicateInvestmentProduct, findInvestmentProduct, formatRange, publicInvestmentProduct,
  updateInvestmentProduct, type InvestmentProduct,
} from '@/editor/investmentProduct';
import { createRowDragController } from '@/editor/extensions/investmentRowDelete';
import ProductRowVersionFlag from './ProductRowVersionFlag';
import { GroupVersionUpdate } from './InvestmentInspector';
import { formatCurrencyRange, formatProductPrice } from '@/lib/format';
import ProductPricingPopover from './ProductPricingPopover';
import InvestmentProductDescription from '@/components/blocks/InvestmentProductDescription';

export default function InvestmentProductGroup({ product, editor, currency, widths, pricesVisible = true }: {
  product: InvestmentProduct; editor: Editor; currency: string;
  widths?: { hourlyRate?: number; hours?: number; subtotal?: number };
  /** Uit wanneer de tabel alleen het totaal toont; de prijsopbouw blijft te openen. */
  pricesVisible?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(false);
  const [overflow, setOverflow] = useState(false);
  const groupRef = useRef<HTMLDivElement>(null);
  const computed = calculateInvestmentProduct(product);
  const projected = publicInvestmentProduct(product);
  const showHours = projected.showHours && pricesVisible;
  const sorter = useRef<ReturnType<typeof createRowDragController> | null>(null);
  useEffect(() => {
    sorter.current = createRowDragController(editor);
    return () => sorter.current?.destroy();
  }, [editor]);
  useEffect(() => {
    const sync = () => {
      const target = findInvestmentProduct(editor, product.id);
      const from = editor.state.selection.from;
      setSelected(Boolean(target && from > target.pos && from < target.pos + target.node.nodeSize));
    };
    sync(); editor.on('selectionUpdate', sync);
    return () => { editor.off('selectionUpdate', sync); };
  }, [editor, product.id]);
  useEffect(() => {
    const handler = (event: Event) => {
      if ((event as CustomEvent).detail === product.id) setOpen(true);
    };
    window.addEventListener('open-product-pricing', handler);
    return () => window.removeEventListener('open-product-pricing', handler);
  }, [product.id]);
  useEffect(() => {
    const root = groupRef.current;
    if (!root) return;
    const observer = new ResizeObserver(() => setOverflow(root.offsetHeight > 500));
    observer.observe(root);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const clear = (event: PointerEvent) => {
      const element = event.target as Element;
      if (element.closest('[data-product-group],.product-pricing-popover,[data-investment-inspector]')) return;
      setSelected(false);
    };
    document.addEventListener('pointerdown', clear);
    return () => document.removeEventListener('pointerdown', clear);
  }, []);
  const select = () => {
    const target = findInvestmentProduct(editor, product.id);
    if (!target) return;
    editor.view.dispatch(editor.state.tr.setSelection(TextSelection.create(editor.state.doc, target.pos + 3)));
    editor.view.focus();
  };
  const change = (next: InvestmentProduct) => updateInvestmentProduct(editor, next);
  const remove = async () => {
    if (!(await confirmProductRemoval(product))) return;
    const target = findInvestmentProduct(editor, product.id);
    if (target) editor.view.dispatch(editor.state.tr.delete(target.pos, target.pos + target.node.nodeSize));
  };
  const duplicate = () => {
    duplicateInvestmentProduct(editor, product.id);
  };
  const money = (cents: number, maxCents?: number) => formatCurrencyRange(cents / 100, maxCents === undefined ? null : maxCents / 100, currency);
  return <div ref={groupRef} data-product-group={product.id}
    className={'investment-product-group' + (selected || open ? ' is-selected' : '')}
    onPointerDown={(event) => { if (!(event.target as Element).closest('button,input,select')) { event.preventDefault(); select(); } }}>
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Anchor asChild><div className={'investment-product-heading' + (projected.lines.length > 0 ? ' has-details' : '')}>
        <button type="button" className="product-expand" aria-label={product.display === 'expanded' ? 'Product inklappen' : 'Product uitklappen'}
          onClick={() => { select(); change({ ...product, display: product.display === 'expanded' ? 'collapsed' : 'expanded' }); }}>
          {product.display === 'expanded' ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
        </button>
        <InvestmentProductDescription title={product.productName} descriptionHtml={projected.descriptionHtml} />
        {computed.adjusted && <span className="investment-product-adjusted">Aangepast</span>}
        <ProductRowVersionFlag productId={product.productId} productVersionId={product.productVersionId} onSelect={select}>
          <GroupVersionUpdate product={product} onChange={(updated) => updateInvestmentProduct(editor, updated, true)} />
        </ProductRowVersionFlag>
        <div className="investment-product-actions">
          <button type="button" aria-label="Product verslepen" title="Product verslepen" onPointerDown={(event) => {
            const target = findInvestmentProduct(editor, product.id);
            const root = groupRef.current;
            if (!target || !root) return;
            root.dataset.investmentRowAnchor = String(target.pos + 3);
            sorter.current?.pointerDown(event.nativeEvent, root, target.pos + 3);
          }}><GripVertical size={14}/></button>
          <button type="button" aria-label="Product dupliceren" title="Product dupliceren" onClick={duplicate}><Copy size={14}/></button>
          <button type="button" aria-label="Product verwijderen" title="Product verwijderen" onClick={remove}><Trash2 size={14}/></button>
        </div>
        <Popover.Trigger asChild><button type="button" className="investment-product-pricing" onClick={select}>Prijsopbouw</button></Popover.Trigger>
        {pricesVisible && <strong className="investment-product-amount">{formatProductPrice(computed, currency)}</strong>}
      </div></Popover.Anchor>
      <Popover.Portal>
        <Popover.Content side="bottom" align="end" sideOffset={6} collisionPadding={12} className="z-[90] outline-none"
          onOpenAutoFocus={(event) => event.preventDefault()} onCloseAutoFocus={(event) => event.preventDefault()}>
          <ProductPricingPopover product={product} currency={currency} onChange={change} onClose={() => { setOpen(false); editor.view.focus(); }} onRemove={remove}/>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
    {overflow && <p role="alert" className="text-xs text-amber-700 py-2">
      De productomschrijving of prijsopbouw past mogelijk niet op één pagina. Verkort de omschrijving of verberg de prijsopbouw met Eén regel.
    </p>}
    {product.display === 'expanded' && <>
      {projected.lines.map((line, index) => {
        const source = computed.lines.filter((entry) => !entry.excluded && !entry.hidden)[index];
        return <div key={index} className={'investment-product-detail' + (showHours ? ' with-hours' : '')}
          style={showHours ? { gap: 0, gridTemplateColumns: `minmax(0,1fr) ${widths?.hourlyRate ?? 100}px ${widths?.hours ?? 80}px ${widths?.subtotal ?? 120}px` }
            : pricesVisible ? undefined : { gridTemplateColumns: 'minmax(0,1fr)' }}>
          <span>{line.description}</span>
          {showHours && <><span className="tabular-nums">{line.rateCents === undefined ? '' : money(line.rateCents)}</span><span className="tabular-nums">{line.hours === undefined ? '' : formatRange(line.hours, line.maxHours, String)}</span></>}
          {pricesVisible && <span className="investment-product-amount">{money(line.totalCents, line.maxTotalCents)}</span>}
          {source && <button type="button" className="product-exclude"
            disabled={computed.lines.filter((entry) => !entry.excluded).length === 1}
            onClick={() => change({ ...product, overrides: { ...product.overrides, [source.code]: { ...product.overrides[source.code], excluded: true } } })}>Uitsluiten</button>}
        </div>;
      })}
      {computed.lines.filter((line) => line.excluded && !line.hidden).map((line) =>
        <div key={line.code} className="investment-product-excluded"><s>{line.description}</s>
          <span>uitgesloten, staat niet in de pdf</span><button type="button" onClick={() => change({
            ...product, overrides: { ...product.overrides, [line.code]: { ...product.overrides[line.code], excluded: false } },
          })}>Terugzetten</button></div>)}
    </>}
  </div>;
}
